import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INCOME_CATEGORIES = ["Booking Income", "Cleaning Fee", "Extra Guest Fee", "Other Income"];
const EXPENSE_CATEGORIES = ["Cleaning", "Maintenance", "Utilities", "Supplies", "Other Expense"];

export default function AddTransaction() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isConsecutiveDates, setIsConsecutiveDates] = useState(false);
  const [isMultipleProperties, setIsMultipleProperties] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);
  const [formData, setFormData] = useState({
    property_id: "",
    type: "income",
    category: "",
    amount: "",
    currency: "INR",
    date: new Date().toISOString().split("T")[0],
    from_date: new Date().toISOString().split("T")[0],
    to_date: new Date().toISOString().split("T")[0],
    description: "",
  });

  useEffect(() => {
    fetchProperties();
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error("Error fetching properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (isMultipleProperties) {
      if (selectedPropertyIds.length === 0) {
        toast.error("Please select at least one property");
        return;
      }
    } else {
      if (!formData.property_id) {
        toast.error("Please select a property");
        return;
      }
    }
    
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      if (isMultipleProperties && isConsecutiveDates) {
        // Multiple properties + Consecutive dates
        const startDate = new Date(formData.from_date);
        const endDate = new Date(formData.to_date);
        
        if (startDate > endDate) {
          toast.error("From date must be before or equal to To date");
          return;
        }

        const dates = [];
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          dates.push(currentDate.toISOString().split("T")[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Create transactions for each property and each date
        const promises = [];
        for (const propertyId of selectedPropertyIds) {
          for (const date of dates) {
            promises.push(
              axios.post(`${API}/transactions`, {
                property_id: propertyId,
                type: formData.type,
                category: formData.category,
                amount: parseFloat(formData.amount),
                currency: formData.currency,
                date: date,
                description: formData.description,
              })
            );
          }
        }

        await Promise.all(promises);
        const totalCount = selectedPropertyIds.length * dates.length;
        toast.success(`${totalCount} transactions added to ${selectedPropertyIds.length} properties!`);
        navigate("/properties");
        
      } else if (isMultipleProperties) {
        // Multiple properties + Single date
        await axios.post(`${API}/transactions/bulk`, {
          property_ids: selectedPropertyIds,
          type: formData.type,
          category: formData.category,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          date: formData.date,
          description: formData.description,
        });
        toast.success(`Transaction added to ${selectedPropertyIds.length} properties!`);
        navigate("/properties");
        
      } else if (isConsecutiveDates) {
        // Single property + Consecutive dates
        const startDate = new Date(formData.from_date);
        const endDate = new Date(formData.to_date);
        
        if (startDate > endDate) {
          toast.error("From date must be before or equal to To date");
          return;
        }

        const dates = [];
        const currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          dates.push(currentDate.toISOString().split("T")[0]);
          currentDate.setDate(currentDate.getDate() + 1);
        }

        const promises = dates.map(date =>
          axios.post(`${API}/transactions`, {
            property_id: formData.property_id,
            type: formData.type,
            category: formData.category,
            amount: parseFloat(formData.amount),
            currency: formData.currency,
            date: date,
            description: formData.description,
          })
        );

        await Promise.all(promises);
        toast.success(`${dates.length} transactions added successfully!`);
        navigate(`/properties/${formData.property_id}`);
        
      } else {
        // Single property + Single date
        await axios.post(`${API}/transactions`, {
          property_id: formData.property_id,
          type: formData.type,
          category: formData.category,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          date: formData.date,
          description: formData.description,
        });
        toast.success("Transaction added successfully!");
        navigate(`/properties/${formData.property_id}`);
      }
    } catch (error) {
      console.error("Error creating transaction:", error);
      toast.error("Failed to add transaction");
    }
  };

  const handlePropertyCheckbox = (propertyId) => {
    setSelectedPropertyIds(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const categories = formData.type === "income" ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" data-testid="add-transaction-loading">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-6" data-testid="no-properties-message">
        <Card className="max-w-md text-center p-8">
          <h2 className="text-2xl font-semibold mb-4">No Properties Found</h2>
          <p className="text-muted-foreground mb-6">You need to add at least one property before you can add transactions.</p>
          <Button onClick={() => navigate("/properties")} className="rounded-full" data-testid="go-to-properties-button">
            Go to Properties
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 max-w-3xl mx-auto animate-fade-in" data-testid="add-transaction-page">
      <div className="mb-8">
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-primary" data-testid="add-transaction-title">
          Add Transaction
        </h1>
        <p className="text-base text-muted-foreground mt-2">Record income or expense for your property</p>
      </div>

      <Card className="bg-white rounded-xl border border-border/60 shadow-sm">
        <CardHeader className="p-6 pb-4">
          <CardTitle className="text-2xl font-semibold text-primary">Transaction Details</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Multiple Properties Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
              <div className="space-y-0.5">
                <Label htmlFor="multiple-properties" className="text-base font-medium">
                  Apply to Multiple Properties
                </Label>
                <p className="text-sm text-muted-foreground">
                  Add the same transaction to multiple properties at once
                </p>
              </div>
              <Switch
                id="multiple-properties"
                checked={isMultipleProperties}
                onCheckedChange={setIsMultipleProperties}
                data-testid="multiple-properties-toggle"
              />
            </div>

            {/* Property Selection */}
            {isMultipleProperties ? (
              <div>
                <Label>Select Properties</Label>
                <div className="mt-2 space-y-2 p-4 border border-border rounded-lg max-h-48 overflow-y-auto" data-testid="properties-checkbox-list">
                  {properties.map((property) => (
                    <div key={property.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`property-${property.id}`}
                        checked={selectedPropertyIds.includes(property.id)}
                        onCheckedChange={() => handlePropertyCheckbox(property.id)}
                        data-testid={`property-checkbox-${property.id}`}
                      />
                      <label
                        htmlFor={`property-${property.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {property.name} - {property.address}
                      </label>
                    </div>
                  ))}
                </div>
                {selectedPropertyIds.length > 0 && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedPropertyIds.length} {selectedPropertyIds.length === 1 ? 'property' : 'properties'} selected
                  </p>
                )}
              </div>
            ) : (
              <div>
                <Label htmlFor="property">Property</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={(value) => setFormData({ ...formData, property_id: value })}
                  required
                >
                  <SelectTrigger className="mt-1" data-testid="property-select">
                    <SelectValue placeholder="Select a property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((property) => (
                      <SelectItem key={property.id} value={property.id} data-testid={`property-option-${property.id}`}>
                        {property.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label htmlFor="type">Transaction Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value, category: "" })}
              >
                <SelectTrigger className="mt-1" data-testid="type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income" data-testid="type-income">Income</SelectItem>
                  <SelectItem value="expense" data-testid="type-expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
                required
              >
                <SelectTrigger className="mt-1" data-testid="category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category} value={category} data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  data-testid="amount-input"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                  <SelectTrigger className="mt-1" data-testid="transaction-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((currency) => (
                      <SelectItem key={currency.code} value={currency.code} data-testid={`txn-currency-${currency.code}`}>
                        {currency.symbol} {currency.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Consecutive Dates Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
              <div className="space-y-0.5">
                <Label htmlFor="consecutive-dates" className="text-base font-medium">
                  Consecutive Dates
                </Label>
                <p className="text-sm text-muted-foreground">
                  Create multiple transactions for a date range
                </p>
              </div>
              <Switch
                id="consecutive-dates"
                checked={isConsecutiveDates}
                onCheckedChange={setIsConsecutiveDates}
                data-testid="consecutive-dates-toggle"
              />
            </div>

            {/* Date Selection */}
            {isConsecutiveDates ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="from-date">From Date</Label>
                  <Input
                    id="from-date"
                    type="date"
                    data-testid="from-date-input"
                    value={formData.from_date}
                    onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="to-date">To Date</Label>
                  <Input
                    id="to-date"
                    type="date"
                    data-testid="to-date-input"
                    value={formData.to_date}
                    onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
            )}

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                data-testid="description-input"
                placeholder="Add notes about this transaction"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
                rows={4}
              />
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1 rounded-full font-medium transition-all duration-200 active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md"
                data-testid="submit-transaction-button"
              >
                Add Transaction
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full font-medium transition-all duration-200 active:scale-95"
                onClick={() => navigate(-1)}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}